import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Row, Col, Spinner, Nav } from 'react-bootstrap';
import { dashboardAPI } from '../../services/api';

function DashboardTab() {
  const [weekData, setWeekData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(getTodayStr());

  function getTodayStr() {
    const d = new Date();
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  const loadWeek = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getWeek(startDate);
      setWeekData(res.data);
      setSelectedDay(0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [startDate]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  const shiftWeek = (delta) => {
    const d = new Date(startDate + 'T12:00:00');
    d.setDate(d.getDate() + (delta * 7));
    setStartDate(formatDateISO(d));
  };

  const goThisWeek = () => {
    setStartDate(getTodayStr());
  };

  const today = getTodayStr();
  const currentDayData = weekData?.days?.[selectedDay] || null;

  return (
    <Card className="fade-in">
      <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span className="fw-bold">🍕 Production Dashboard</span>
        <div className="d-flex align-items-center gap-2">
          <Button size="sm" variant="outline-secondary" onClick={() => shiftWeek(-1)}>← Prev Week</Button>
          <Button size="sm" variant="outline-secondary" onClick={goThisWeek}>This Week</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => shiftWeek(1)}>Next Week →</Button>
        </div>
      </Card.Header>

      {/* Day tabs */}
      {weekData && (
        <div style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
          <Nav variant="pills" className="px-3 pt-2 pb-0 flex-nowrap" style={{ gap: '4px', overflowX: 'auto' }}>
            {weekData.days.map((day, idx) => {
              const isToday = day.date === today;
              const hasOrders = day.slots.some(s => s.orders.length > 0);
              const isProjected = !day.has_actual_orders && hasOrders;
              const isSelected = selectedDay === idx;

              return (
                <Nav.Item key={day.date}>
                  <Nav.Link
                    active={isSelected}
                    onClick={() => setSelectedDay(idx)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '0.82rem',
                      borderRadius: '6px 6px 0 0',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      backgroundColor: isSelected ? '#fff' : 'transparent',
                      borderBottom: isSelected ? '2px solid #c44b25' : '2px solid transparent',
                      color: isSelected ? '#1a1a1a' : '#6b6560',
                      fontWeight: isSelected || isToday ? 600 : 400
                    }}
                  >
                    <div>{day.day_name.substring(0, 3)}</div>
                    <div style={{ fontSize: '0.72rem', color: isToday ? '#c44b25' : '#999' }}>
                      {formatShortDate(day.date)}
                    </div>
                    <div style={{ fontSize: '0.65rem', marginTop: '2px' }}>
                      {isToday && <span className="text-danger">● </span>}
                      {hasOrders && !isProjected && <span style={{ color: '#2d7a3a' }}>✓</span>}
                      {isProjected && <span style={{ color: '#b8860b' }}>○</span>}
                      {!hasOrders && <span style={{ color: '#ccc' }}>—</span>}
                    </div>
                  </Nav.Link>
                </Nav.Item>
              );
            })}
          </Nav>
        </div>
      )}

      <Card.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading orders...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-5 text-danger">
            <h5>⚠️ {error}</h5>
          </div>
        )}

        {!loading && !error && currentDayData && (
          <>
            {/* Day header */}
            <div className="d-flex align-items-center gap-3 mb-3">
              <h5 className="mb-0 fw-bold">{currentDayData.day_name} — {formatLongDate(currentDayData.date)}</h5>
              {currentDayData.date === today && (
                <Badge bg="danger" style={{ fontSize: '0.7rem' }}>Today</Badge>
              )}
              {!currentDayData.has_actual_orders && currentDayData.slots.some(s => s.orders.length > 0) && (
                <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>Projected from standing orders</Badge>
              )}
            </div>

            <DayContent data={currentDayData} />
          </>
        )}
      </Card.Body>
    </Card>
  );
}

// ── Helpers ──

function formatDateISO(d) {
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

function formatShortDate(dateStr) {
  const [, m, d] = dateStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}`;
}

function formatLongDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ── Day Content ──

function DayContent({ data }) {
  const hasAnyOrders = data.slots.some(s => s.orders.length > 0);

  if (!hasAnyOrders) {
    return (
      <div className="text-center py-5 text-muted">
        <h4>📭</h4>
        <h5>No orders for this date</h5>
        <p>No submitted orders or standing orders found.</p>
      </div>
    );
  }

  return (
    <div>
      {data.slots.map(slot => (
        <SlotSection key={slot.slot_id} slot={slot} />
      ))}
    </div>
  );
}

// ── Slot Section ──

function SlotSection({ slot }) {
  const [expanded, setExpanded] = useState(true);
  const orderCount = slot.orders.length;
  const deviationCount = slot.orders.filter(o => o.has_deviations).length;
  const projectedCount = slot.orders.filter(o => o.projected).length;

  if (orderCount === 0 && slot.summary.length === 0) return null;

  const cutoff = slot.cutoff_time ? slot.cutoff_time.substring(0, 5) : '';
  const reqTime = slot.required_time ? slot.required_time.substring(0, 5) : '';

  return (
    <div style={{ marginBottom: '28px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          paddingBottom: '8px',
          borderBottom: '2px solid #333',
          marginBottom: '14px',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <h5 className="mb-0 fw-bold">{slot.slot_name}</h5>
        <span className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {[cutoff && `cutoff ${cutoff}`, reqTime && `needed by ${reqTime}`].filter(Boolean).join(' · ')}
        </span>
        <span className="text-muted ms-auto" style={{ fontSize: '0.85rem' }}>
          {orderCount} order{orderCount !== 1 ? 's' : ''}
          {projectedCount > 0 && projectedCount === orderCount && (
            <span className="ms-1" style={{ color: '#b8860b' }}>(projected)</span>
          )}
          {deviationCount > 0 && (
            <span className="text-warning ms-2">
              ⚠ {deviationCount} deviation{deviationCount !== 1 ? 's' : ''}
            </span>
          )}
          <span className="ms-2">{expanded ? '▾' : '▸'}</span>
        </span>
      </div>

      {expanded && (
        <>
          {slot.summary.length > 0 && <SummaryCard summary={slot.summary} />}
          {slot.orders.map((order, i) => (
            <StoreCard key={order.id || `proj-${i}`} order={order} />
          ))}
        </>
      )}
    </div>
  );
}

// ── Summary Card ──

function SummaryCard({ summary }) {
  const catColors = { HOT: '#c44b25', COLD: '#2563a8', MISC: '#6b6560' };
  const catLabels = { COLD: '❄️ Cold', HOT: '🔥 Hot', MISC: '📦 Misc' };
  const catOrder = ['COLD', 'HOT', 'MISC'];

  const grouped = {};
  for (const item of summary) {
    const cat = item.category || 'MISC';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }

  const grandTotal = summary.reduce((sum, item) => sum + item.total_quantity, 0);

  const sortedCats = Object.keys(grouped).sort((a, b) =>
    (catOrder.indexOf(a) === -1 ? 99 : catOrder.indexOf(a)) - (catOrder.indexOf(b) === -1 ? 99 : catOrder.indexOf(b))
  );

  return (
    <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
      <Card.Body className="py-2 px-3">
        <div className="text-muted fw-bold mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          📊 Total Pizzas <span style={{ fontSize: '1.1rem', color: '#333', fontWeight: 800 }}>{grandTotal}</span>
        </div>
        <Row>
          {sortedCats.map(cat => (
            <Col key={cat} xs="auto">
              <div className="mb-1" style={{ fontSize: '0.8rem', fontWeight: 600, color: catColors[cat] || '#333' }}>
                {catLabels[cat] || cat}
              </div>
              {grouped[cat].map(item => (
                <div key={item.item_name} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', fontSize: '0.9rem' }}>
                  <span style={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    minWidth: '26px',
                    textAlign: 'right',
                    color: catColors[cat] || '#333'
                  }}>
                    {item.total_quantity}
                  </span>
                  <span>{item.item_name}</span>
                </div>
              ))}
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  );
}

// ── Store Card ──

function StoreCard({ order }) {
  const badgeVariants = {
    standing: { bg: '#e8f5ea', color: '#2d7a3a', label: 'Standing' },
    manual: { bg: '#eef4fb', color: '#2563a8', label: 'Manual' },
    modified: { bg: '#fdf6e3', color: '#b8860b', label: 'Modified' }
  };
  const badge = badgeVariants[order.order_type] || badgeVariants.manual;

  const devMap = {};
  if (order.deviations) {
    for (const d of order.deviations) devMap[d.menu_item_id] = d;
  }

  const isSkipped = order.status === 'skipped';

  return (
    <Card
      className="mb-2"
      style={{
        borderLeft: isSkipped ? '4px solid #dc3545' :
                    order.projected ? '4px solid #6c757d' :
                    order.has_deviations ? '4px solid #b8860b' : undefined,
        backgroundColor: isSkipped ? '#fff5f5' :
                         order.projected ? '#f9f9f9' :
                         order.has_deviations ? '#fffcf2' : undefined,
        opacity: isSkipped ? 0.7 : order.projected ? 0.85 : 1
      }}
    >
      <Card.Body className="py-2 px-3">
        {/* Header */}
        <div className="d-flex align-items-center gap-2 mb-2">
          <strong style={{ fontSize: '0.95rem' }}>{order.location_name}</strong>
          <span style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: '10px',
            backgroundColor: isSkipped ? '#f8d7da' : order.projected ? '#e9ecef' : badge.bg,
            color: isSkipped ? '#842029' : order.projected ? '#6c757d' : badge.color,
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}>
            {isSkipped ? '⏭️ Skipped' : order.projected ? 'Projected' : badge.label}
          </span>
          {!order.projected && !isSkipped && (
            <span className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {order.status}
            </span>
          )}
          {order.has_deviations && (
            <Badge bg="warning" text="dark" className="ms-auto" style={{ fontSize: '0.7rem' }}>
              ⚠ {order.deviations.length} change{order.deviations.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Items grouped by category */}
        {(() => {
          const catLabels = { HOT: '🔥', COLD: '❄️', MISC: '📦' };
          const catColors = { HOT: '#c44b25', COLD: '#2563a8', MISC: '#6b6560' };
          const catOrder = ['COLD', 'HOT', 'MISC'];

          // Group items by category
          const grouped = {};
          for (const item of order.items) {
            const cat = item.category || 'MISC';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(item);
          }

          // Group removed deviations by category
          const removedDevs = {};
          if (order.deviations) {
            for (const d of order.deviations.filter(d => d.ordered === 0 && d.expected > 0)) {
              const cat = d.category || 'MISC';
              if (!removedDevs[cat]) removedDevs[cat] = [];
              removedDevs[cat].push(d);
            }
          }

          const allCats = [...new Set([...Object.keys(grouped), ...Object.keys(removedDevs)])];
          allCats.sort((a, b) => (catOrder.indexOf(a) === -1 ? 99 : catOrder.indexOf(a)) - (catOrder.indexOf(b) === -1 ? 99 : catOrder.indexOf(b)));

          return (
            <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
              {allCats.map(cat => (
                <div key={cat}>
                  <div style={{ fontSize: '0.72rem', color: catColors[cat] || '#666', fontWeight: 600, marginBottom: '2px' }}>
                    {catLabels[cat] || '📦'} {cat}
                  </div>
                  {(grouped[cat] || []).map(item => {
                    const dev = devMap[item.menu_item_id];
                    return (
                      <div key={item.menu_item_id} style={{
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '4px',
                        fontWeight: dev ? 600 : 400
                      }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{item.quantity}×</span>
                        <span>{item.item_name}</span>
                        {dev && dev.expected === 0 && (
                          <span style={{ fontSize: '0.7rem', fontFamily: 'monospace', color: '#2563a8' }}>+new</span>
                        )}
                        {dev && dev.expected > 0 && (
                          <span style={{
                            fontSize: '0.7rem',
                            fontFamily: 'monospace',
                            color: dev.diff > 0 ? '#2d7a3a' : '#c44b25'
                          }}>
                            {dev.diff > 0 ? '+' : ''}{dev.diff}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {/* Removed items in this category */}
                  {(removedDevs[cat] || []).map(d => (
                    <div key={d.menu_item_id} style={{
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '4px',
                      fontWeight: 600,
                      textDecoration: 'line-through',
                      color: '#c44b25'
                    }}>
                      <span style={{ fontFamily: 'monospace' }}>{d.expected}×</span>
                      <span>{d.item_name}</span>
                      <span style={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>removed</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Deviation details */}
        {order.has_deviations && order.deviations.length > 0 && (
          <div style={{
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px dashed #ddd',
            fontSize: '0.8rem',
            color: '#8a7000'
          }}>
            <div style={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
              Changes from standing order
            </div>
            {order.deviations.map(d => {
              let desc = '';
              if (d.expected === 0) desc = `${d.item_name}: added ${d.ordered}`;
              else if (d.ordered === 0) desc = `${d.item_name}: removed (was ${d.expected})`;
              else desc = `${d.item_name}: ${d.expected} → ${d.ordered}`;
              return <div key={d.menu_item_id} className="text-muted">{desc}</div>;
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default DashboardTab;
